import type { GameState, GameAction } from '../game/types';
import { BUILT_IN_DECKS, pickFallbackDeck } from '../game/decks';
import { encodeDeckToUrl } from '../game/share';
import { generateDeck, testConnection, LocationUnsupportedError, KeyForbiddenError, MissingKeyError } from '../services/gemini';
import { Icons } from './Icons';
import { Notification } from './Notification';
import { SaveModal } from './SaveModal';
import { ShareModal } from './ShareModal';
import { ConfirmModal } from './ConfirmModal';
import { ApiKeyModal } from './ApiKeyModal';

type Props = {
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
  onPlaySfx: (type: 'click' | 'flip' | 'match') => void;
};

const DEMO_PAIRS = [
  { q: '蘋果', a: 'Apple' },
  { q: '香蕉', a: 'Banana' },
  { q: '貓', a: 'Cat' },
  { q: '狗', a: 'Dog' },
  { q: '紅', a: 'Red' },
  { q: '藍', a: 'Blue' },
  { q: '大', a: 'Big' },
  { q: '小', a: 'Small' },
];

export function SetupScreen({ state, dispatch, onPlaySfx }: Props) {
  const handleSelectBuiltIn = (key: string) => {
    const pairs = BUILT_IN_DECKS[key];
    if (!pairs) return;
    dispatch({ type: 'SET_CARD_PAIRS', pairs });
    dispatch({ type: 'SET_AI_TOPIC', topic: key });
    onPlaySfx('match');
    dispatch({ type: 'SET_NOTIFICATION', message: `📚 已載入：${key}` });
  };

  const handleAiGenerate = async () => {
    onPlaySfx('click');
    if (!state.aiTopic.trim()) {
      dispatch({ type: 'AI_GEN_ERROR', message: '請輸入主題' });
      return;
    }
    dispatch({ type: 'AI_GEN_START' });
    try {
      const pairs = await generateDeck(state.aiTopic, state.customApiKey);
      dispatch({ type: 'AI_GEN_SUCCESS', pairs });
      onPlaySfx('match');
    } catch (e) {
      let msg = 'API 連線失敗';
      if (e instanceof LocationUnsupportedError) msg = '目前地區不支援免費版連線';
      else if (e instanceof KeyForbiddenError) msg = 'API 權限不足或未啟用';
      else if (e instanceof MissingKeyError) msg = '請先設定 API 金鑰';
      dispatch({ type: 'AI_GEN_ERROR', message: msg });
      // fallback：1 秒後載入本地題庫
      setTimeout(() => {
        const fallback = pickFallbackDeck(state.aiTopic);
        dispatch({
          type: 'AI_GEN_FALLBACK',
          pairs: fallback,
          message: '⚠️ AI 累了，已載入精選題庫',
        });
        onPlaySfx('match');
      }, 1000);
    }
  };

  const handleShare = () => {
    onPlaySfx('click');
    if (state.cardPairs.some((p) => !p.q && !p.a)) {
      dispatch({ type: 'SET_SETUP_ERROR', message: '⚠️ 請先填寫題目' });
      return;
    }
    const link = encodeDeckToUrl(state.cardPairs);
    dispatch({ type: 'OPEN_SHARE_MODAL', link });
  };

  const handleOpenSave = () => {
    onPlaySfx('click');
    if (state.cardPairs.every((p) => !p.q && !p.a)) {
      dispatch({ type: 'SET_SETUP_ERROR', message: '⚠️ 無內容可存' });
      return;
    }
    dispatch({ type: 'OPEN_SAVE_MODAL', defaultName: state.aiTopic || '' });
  };

  const handleSaveConfirm = () => {
    if (!state.saveName.trim()) return;
    dispatch({ type: 'SAVE_DECK', name: state.saveName });
    onPlaySfx('flip');
  };

  const handleLoadDeck = (name: string) => {
    onPlaySfx('click');
    dispatch({
      type: 'OPEN_CONFIRM',
      message: `載入「${name}」？`,
      onConfirm: () => {
        const pairs = state.savedDecks[name];
        if (pairs) dispatch({ type: 'SET_CARD_PAIRS', pairs });
        dispatch({ type: 'CLOSE_CONFIRM' });
        onPlaySfx('flip');
      },
    });
  };

  const handleDeleteDeck = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch({
      type: 'OPEN_CONFIRM',
      message: `刪除「${name}」？`,
      onConfirm: () => {
        dispatch({ type: 'DELETE_DECK', name });
      },
    });
  };

  const handleStart = () => {
    if (state.cardPairs.some((p) => !p.q || !p.a)) {
      dispatch({ type: 'SET_SETUP_ERROR', message: '⚠️ 請填滿卡牌！' });
      return;
    }
    dispatch({ type: 'START_GAME' });
  };

  const handleTestKey = async (key: string) => {
    if (!key) {
      dispatch({ type: 'SET_NOTIFICATION', message: '請先輸入金鑰' });
      return;
    }
    dispatch({ type: 'SET_NOTIFICATION', message: '測試連線中...' });
    try {
      await testConnection(key);
      dispatch({ type: 'SET_NOTIFICATION', message: '✅ 連線成功！' });
    } catch (e) {
      let msg = e instanceof Error ? e.message : String(e);
      if (e instanceof LocationUnsupportedError) msg = '台灣地區限制 (請使用付費版金鑰或VPN)';
      if (e instanceof KeyForbiddenError) msg = 'API 權限不足或未啟用';
      dispatch({ type: 'SET_NOTIFICATION', message: `❌ 連線失敗: ${msg}` });
    }
  };

  const copyLink = () => {
    void navigator.clipboard.writeText(state.shareLink).then(() => alert('已複製！'));
  };

  return (
    <div className="h-dvh flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-[clamp(1rem,2.5vh,2rem)] max-h-full w-full max-w-4xl border-4 border-green-200 animate-fade-in relative flex flex-col overflow-hidden">
        <Notification message={state.notification} />
        <button
          onClick={() => dispatch({ type: 'GO_TO_INTRO' })}
          className="absolute top-[clamp(0.75rem,2vh,1.5rem)] left-[clamp(0.75rem,2vh,1.5rem)] text-gray-400 hover:text-gray-600 font-bold flex items-center gap-1 z-10"
        >
          ← 回首頁
        </button>
        <button
          onClick={() => dispatch({ type: 'OPEN_KEY_MODAL' })}
          className={`absolute top-[clamp(0.75rem,2vh,1.5rem)] right-[clamp(0.75rem,2vh,1.5rem)] font-bold flex items-center gap-1 z-10 transition px-3 py-1 rounded-full ${
            state.customApiKey
              ? 'bg-green-100 text-green-700 border border-green-300'
              : 'bg-red-100 text-red-600 border border-red-300 animate-pulse'
          }`}
        >
          <Icons.Key /> {state.customApiKey ? '已設定金鑰' : '設定 AI 金鑰'}
        </button>
        <h2 className="shrink-0 text-[clamp(1.25rem,3.5vh,1.875rem)] font-black text-center text-green-700 mb-[clamp(0.5rem,1.5vh,1rem)] mt-[clamp(1.5rem,4vh,2rem)]">
          📝 設計題目卡牌
        </h2>

        <div className="shrink-0 grid grid-cols-1 lg:grid-cols-3 gap-[clamp(0.5rem,1.5vh,1rem)] mb-[clamp(0.5rem,1.5vh,1rem)]">
          <div className="lg:col-span-2 bg-blue-50 p-[clamp(0.5rem,1.5vh,1rem)] rounded-2xl border-2 border-blue-100">
            <label className="block text-indigo-700 font-bold mb-2 flex items-center gap-2">
              <Icons.Book /> 📚 快速選擇題庫 (保證可用)
            </label>
            <div className="flex flex-wrap gap-2 mb-[clamp(0.5rem,1.5vh,1rem)]">
              {Object.keys(BUILT_IN_DECKS).map((key) => (
                <button
                  key={key}
                  onClick={() => handleSelectBuiltIn(key)}
                  className="bg-white border border-blue-200 text-blue-700 px-3 py-1 rounded-full text-sm hover:bg-blue-100 hover:border-blue-300 transition shadow-sm btn-3d"
                >
                  {key}
                </button>
              ))}
            </div>

            <label className="block text-indigo-700 font-bold mb-2 flex items-center gap-2">
              <Icons.Sparkles /> ✨ AI 魔法出題 (Beta)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="輸入主題 (AI 失敗將自動切換題庫)"
                className="flex-1 p-2 border-2 border-white rounded-xl min-w-0"
                value={state.aiTopic}
                onChange={(e) => dispatch({ type: 'SET_AI_TOPIC', topic: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleAiGenerate();
                }}
              />
              <button
                onClick={() => void handleAiGenerate()}
                disabled={state.isGeneratingPairs}
                className="bg-indigo-500 text-white px-4 rounded-xl font-bold btn-3d shrink-0"
              >
                {state.isGeneratingPairs ? '...' : '生成'}
              </button>
            </div>
            {state.aiStatusMessage && (
              <p className="text-blue-500 text-xs mt-1 animate-pulse font-bold">
                {state.aiStatusMessage}
              </p>
            )}
            {state.aiError && <p className="text-red-500 text-xs mt-1">{state.aiError}</p>}
          </div>
          <div className="bg-amber-50 p-[clamp(0.5rem,1.5vh,1rem)] rounded-2xl border-2 border-amber-100 flex flex-col">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-amber-800">💾 存檔 & 分享</h3>
              <div className="flex gap-1">
                <button
                  onClick={handleShare}
                  className="text-xs bg-blue-200 text-blue-900 px-2 py-1 rounded-lg font-bold flex items-center gap-1 hover:bg-blue-300 transition"
                  title="產生分享連結"
                >
                  <Icons.Share /> 分享
                </button>
                <button
                  onClick={handleOpenSave}
                  className="text-xs bg-amber-200 text-amber-900 px-2 py-1 rounded-lg font-bold hover:bg-amber-300 transition flex items-center gap-1"
                >
                  <Icons.Save /> 存檔
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1 pr-1 max-h-[80px]">
              {Object.keys(state.savedDecks).map((n) => (
                <div
                  key={n}
                  className="flex justify-between bg-white p-1.5 rounded cursor-pointer hover:bg-amber-100"
                  onClick={() => handleLoadDeck(n)}
                >
                  <span className="text-xs font-bold truncate max-w-[80px]">{n}</span>
                  <button
                    onClick={(e) => handleDeleteDeck(n, e)}
                    className="text-gray-300 hover:text-red-500"
                  >
                    <Icons.Trash />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col mb-[clamp(0.5rem,1.5vh,1rem)] relative">
          <button
            onClick={() => {
              dispatch({ type: 'SET_CARD_PAIRS', pairs: DEMO_PAIRS });
              onPlaySfx('flip');
            }}
            className="absolute -top-5 right-0 text-xs text-gray-400 hover:text-gray-600 underline z-10"
          >
            填入範例
          </button>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1 grid grid-cols-1 gap-2 auto-rows-min">
            {state.cardPairs.map((p, i) => (
              <div
                key={i}
                className="flex gap-2 items-center bg-gray-50 p-2 rounded-xl border border-gray-200 shadow-sm"
              >
                <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold shrink-0">
                  {i + 1}
                </span>
                <input
                  type="text"
                  placeholder="題目"
                  className="w-full p-1.5 bg-white rounded-lg text-center font-medium shadow-inner outline-none focus:ring-1 ring-green-300 min-w-0"
                  value={p.q}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_CARD_PAIR', index: i, field: 'q', value: e.target.value })
                  }
                />
                <span className="text-gray-300 shrink-0">↔</span>
                <input
                  type="text"
                  placeholder="答案"
                  className="w-full p-1.5 bg-white rounded-lg text-center font-medium shadow-inner outline-none focus:ring-1 ring-green-300 min-w-0"
                  value={p.a}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_CARD_PAIR', index: i, field: 'a', value: e.target.value })
                  }
                />
              </div>
            ))}
          </div>
        </div>
        {state.setupError && (
          <div className="shrink-0 text-red-500 text-center font-bold mb-2 animate-bounce">
            {state.setupError}
          </div>
        )}
        <button
          onClick={handleStart}
          className="shrink-0 w-full bg-gradient-to-b from-green-500 to-green-600 border-b-4 border-green-700 text-white font-black py-[clamp(0.5rem,1.5vh,0.75rem)] rounded-2xl text-[clamp(1rem,2.5vh,1.25rem)] shadow-lg btn-3d"
        >
          開始遊戲 GO!
        </button>

        <SaveModal
          open={state.saveModalOpen}
          name={state.saveName}
          onChange={(v) => dispatch({ type: 'SET_SAVE_NAME', name: v })}
          onCancel={() => dispatch({ type: 'CLOSE_SAVE_MODAL' })}
          onConfirm={handleSaveConfirm}
        />
        <ShareModal
          open={state.shareModalOpen}
          link={state.shareLink}
          onClose={() => dispatch({ type: 'CLOSE_SHARE_MODAL' })}
          onCopy={copyLink}
        />
        <ConfirmModal
          open={state.confirmModal.open}
          message={state.confirmModal.message}
          onCancel={() => dispatch({ type: 'CLOSE_CONFIRM' })}
          onConfirm={() => state.confirmModal.onConfirm?.()}
        />
        <ApiKeyModal
          open={state.keyModalOpen}
          initialKey={state.customApiKey}
          hasStoredKey={Boolean(state.customApiKey)}
          onClose={() => dispatch({ type: 'CLOSE_KEY_MODAL' })}
          onSave={(key) => {
            dispatch({ type: 'SET_API_KEY', key });
            dispatch({ type: 'CLOSE_KEY_MODAL' });
            dispatch({ type: 'SET_NOTIFICATION', message: '🔑 金鑰已儲存！' });
          }}
          onClear={() => dispatch({ type: 'CLEAR_API_KEY' })}
          onTest={(key) => void handleTestKey(key)}
        />
      </div>
    </div>
  );
}
